import type { ResolvedImage } from '@tsagko/label-contracts'

function bitAt(buf: Uint8Array, bytesPerRow: number, x: number, y: number): 0 | 1 {
  const byte = buf[y * bytesPerRow + (x >> 3)] ?? 0
  return ((byte >> (7 - (x & 7))) & 1) as 0 | 1
}

/**
 * Rotate a packed 1-bit, row-major, MSB-first bitmap by 90° (CW) or 270° (CCW).
 * Source w×h with ceil(w/8) bytes/row; result swaps dims to h×w. Matches the
 * whole-label box remap:
 *   90  (CW):  dst(nx,ny) ← src(sx=ny,     sy=h-1-nx)
 *   270 (CCW): dst(nx,ny) ← src(sx=w-1-ny, sy=nx)
 */
export function rotateMonoBitmap(
  buffer: Uint8Array,
  widthDots: number,
  heightDots: number,
  deg: 90 | 270
): { buffer: Uint8Array; widthDots: number; heightDots: number } {
  const w = widthDots
  const h = heightDots
  const srcBpr = Math.ceil(w / 8)
  const nw = h
  const nh = w
  const dstBpr = Math.ceil(nw / 8)
  const out = new Uint8Array(dstBpr * nh)
  for (let ny = 0; ny < nh; ny++) {
    for (let nx = 0; nx < nw; nx++) {
      const sx = deg === 90 ? ny : w - 1 - ny
      const sy = deg === 90 ? h - 1 - nx : nx
      if (bitAt(buffer, srcBpr, sx, sy)) {
        const oi = ny * dstBpr + (nx >> 3)
        out[oi] = (out[oi] ?? 0) | (0x80 >> (nx & 7))
      }
    }
  }
  return { buffer: out, widthDots: nw, heightDots: nh }
}

/** Rotate every resolved image's raster by `deg`; identity for deg 0. */
export function rotateResolvedImages(
  images: Map<string, ResolvedImage>,
  deg: 0 | 90 | 270
): Map<string, ResolvedImage> {
  if (deg === 0) return images
  const out = new Map<string, ResolvedImage>()
  for (const [id, img] of images) {
    const r = rotateMonoBitmap(img.buffer, img.widthDots, img.heightDots, deg)
    out.set(id, { buffer: r.buffer, widthDots: r.widthDots, heightDots: r.heightDots })
  }
  return out
}
